import { useMemo, useState } from "react";
import {
    Autocomplete, Box, Button, Card, CardContent, Chip, Divider, List, ListItem,
    ListItemText, Stack, TextField, Typography, CircularProgress, Alert
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import { useDialogs } from "@toolpad/core";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";

interface AdminUser {
    id: number;
    email: string;
    username: string | null;
}

interface AdminWorkspace {
    id: number;
    name: string;
}

interface SuppressionEntry {
    type: string;
    address: string;
    reason: string | null;
    timestamp: number | null;
}

const CLEAR_ALL_CONFIRM_TEXT = "CLEAR ALL";

export default function AdminPostalSuppression() {
    const dialogs = useDialogs();
    const { enqueueSnackbar } = useSnackbar();

    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
    const [selectedWorkspace, setSelectedWorkspace] = useState<AdminWorkspace | null>(null);
    const [clearAllText, setClearAllText] = useState("");

    const usersQuery = useQuery({
        queryKey: ["adminSuppressionUsers"],
        queryFn: () => authenticateAndFetchData("/admin/get-users/"),
        retry: retryFn,
    });
    const users: AdminUser[] = usersQuery.data?.data?.users || [];

    const workspacesQuery = useQuery({
        queryKey: ["adminSuppressionWorkspaces", selectedUser?.id],
        queryFn: () => authenticateAndFetchData(
            `/admin/get-user-workspaces/?user_id=${selectedUser?.id}&page_size=500`
        ),
        retry: retryFn,
        enabled: !!selectedUser,
    });
    const workspaces: AdminWorkspace[] = workspacesQuery.data?.data?.data || [];

    const infoQuery = useQuery({
        queryKey: ["adminSuppressionInfo", selectedWorkspace?.id],
        queryFn: () => authenticateAndFetchData(
            `/admin/postal-suppression/info/?workspace_id=${selectedWorkspace?.id}`
        ),
        retry: retryFn,
        enabled: !!selectedWorkspace,
    });
    const count: number = infoQuery.data?.data?.count ?? 0;
    const sample: SuppressionEntry[] = infoQuery.data?.data?.sample || [];

    const clearMutation = useMutation({
        mutationFn: (payload: object) => authenticateAndPostData("/admin/postal-suppression/clear/", payload),
        retry: retryFn,
        onSuccess: (res) => {
            enqueueSnackbar(res.data?.message || "Suppression list clear started.", { variant: "success" });
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(error?.data?.message || "Failed to clear suppression list.", { variant: "error" });
        },
    });

    const onClearSingle = async () => {
        if (!selectedWorkspace) return;
        const confirmed = await dialogs.confirm(
            `This will permanently clear ${count} suppressed address(es) for workspace "${selectedWorkspace.name}". This action cannot be undone.`,
            { title: "Clear Suppression List", cancelText: "Cancel", okText: "Clear" }
        );
        if (confirmed) {
            clearMutation.mutate({ workspace_id: selectedWorkspace.id }, {
                onSuccess: () => {
                    [2000, 5000].forEach((delay) => setTimeout(() => infoQuery.refetch(), delay));
                },
            });
        }
    };

    const onClearAll = async () => {
        const confirmed = await dialogs.confirm(
            "This will permanently clear suppression lists across ALL mailservers. This action cannot be undone.",
            { title: "Clear ALL Suppression Lists", cancelText: "Cancel", okText: "Clear All" }
        );
        if (confirmed) {
            clearMutation.mutate({ clear_all: true }, {
                onSuccess: () => setClearAllText(""),
            });
        }
    };

    const clearAllEnabled = useMemo(
        () => clearAllText.trim().toUpperCase() === CLEAR_ALL_CONFIRM_TEXT && !clearMutation.isPending,
        [clearAllText, clearMutation.isPending]
    );

    return (
        <Box sx={{ p: 3, maxWidth: 800 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <BlockIcon color="primary" />
                <Typography variant="h5">Postal Suppression List</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Clear the Postal suppression list for a specific workspace's mailserver, or across all
                mailservers. Suppressions block sending to recipients that previously hard-bounced.
            </Typography>

            <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Clear a single mailserver</Typography>
                    <Stack spacing={3}>
                        <Autocomplete
                            options={users}
                            loading={usersQuery.isLoading}
                            getOptionLabel={(option) => `${option.username || "No Username"} (${option.email})`}
                            value={selectedUser}
                            onChange={(_, newValue) => {
                                setSelectedUser(newValue);
                                setSelectedWorkspace(null);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Select User" variant="outlined" />
                            )}
                        />

                        <Autocomplete
                            options={workspaces}
                            loading={workspacesQuery.isLoading}
                            disabled={!selectedUser}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedWorkspace}
                            onChange={(_, newValue) => setSelectedWorkspace(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Workspace" variant="outlined" />
                            )}
                        />

                        {selectedWorkspace && (
                            <Box>
                                {infoQuery.isLoading ? (
                                    <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <CircularProgress size={32} />
                                            <Stack>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    Fetching suppression list...
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Retrieving data from Postal mailserver
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                ) : infoQuery.isError ? (
                                    <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            Could not load suppression info
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            Failed to retrieve suppression list data from Postal. Please try again.
                                        </Typography>
                                    </Alert>
                                ) : (
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Suppressed addresses:
                                            </Typography>
                                            <Chip
                                                label={count}
                                                color={count > 0 ? "warning" : "default"}
                                                size="small"
                                                variant="filled"
                                            />
                                        </Stack>
                                        {sample.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Sample of suppressed addresses:
                                                </Typography>
                                                <List dense sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
                                                    {sample.map((s, i) => (
                                                        <ListItem key={`${s.address}-${i}`}>
                                                            <ListItemText
                                                                primary={s.address}
                                                                secondary={s.reason || "No reason provided"}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<DeleteSweepIcon />}
                                    sx={{ mt: 2 }}
                                    disabled={clearMutation.isPending || infoQuery.isLoading || count === 0}
                                    onClick={onClearSingle}
                                >
                                    Clear Suppression List
                                </Button>
                            </Box>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderColor: "error.main" }}>
                <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <WarningAmberIcon color="error" />
                        <Typography variant="h6" color="error">Danger zone — clear ALL mailservers</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Clears suppression lists across every mailserver on Postal. This cannot be undone.
                        Type <b>{CLEAR_ALL_CONFIRM_TEXT}</b> to enable the button.
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            size="small"
                            placeholder={CLEAR_ALL_CONFIRM_TEXT}
                            value={clearAllText}
                            onChange={(e) => setClearAllText(e.target.value)}
                            sx={{ width: 220 }}
                        />
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<DeleteSweepIcon />}
                            disabled={!clearAllEnabled}
                            onClick={onClearAll}
                        >
                            Clear All Mailservers
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}
